import { OnboardingPeriodBenefitsDto } from '@zorro/clients';
import {
  getFamilyUnitFromWhoIsEnrolled,
  getWhoIsEnrolled,
} from '@zorro/web/ui-utils';
import Decimal from 'decimal.js';
import { useEffect } from 'react';

import {
  EnrollmentAllowanceStorePlan,
  useEnrollmentAllowanceStore,
} from './EnrollmentAllowanceStore';

const toStorePlan = (benefit: {
  id: string;
  plan: { premium: number; carrierName: string };
}): EnrollmentAllowanceStorePlan => ({
  planId: benefit.id,
  premium: benefit.plan.premium,
  carrierName: benefit.plan.carrierName,
});

const withEditedPremium = (
  plans: EnrollmentAllowanceStorePlan[],
  editedBenefitId: string | undefined,
  editedBenefitPremium: number | undefined
): EnrollmentAllowanceStorePlan[] => {
  if (!editedBenefitId || editedBenefitPremium === undefined) return plans;
  const numericPremium = new Decimal(editedBenefitPremium || 0).toNumber();
  return plans.map((plan) =>
    plan.planId === editedBenefitId
      ? { ...plan, premium: numericPremium }
      : plan
  );
};

export const useHydrateEnrollmentAllowanceStoreFromBenefits = (
  benefitsData: OnboardingPeriodBenefitsDto | undefined,
  editedBenefitId?: string,
  editedBenefitPremium?: number
) => {
  const setMedicalPlans = useEnrollmentAllowanceStore(
    (state) => state.setMedicalPlans
  );
  const setDentalPlans = useEnrollmentAllowanceStore(
    (state) => state.setDentalPlans
  );
  const setVisionPlans = useEnrollmentAllowanceStore(
    (state) => state.setVisionPlans
  );
  const setFamilyUnit = useEnrollmentAllowanceStore(
    (state) => state.setFamilyUnit
  );
  const setNumberOfDependents = useEnrollmentAllowanceStore(
    (state) => state.setNumberOfDependents
  );

  useEffect(() => {
    if (!benefitsData) return;

    const insured = benefitsData.majorMedicalBenefit?.insuredPeople;
    if (insured) {
      const whoIsEnrolled = getWhoIsEnrolled(insured);
      const { familyUnit, numberOfDependents } = getFamilyUnitFromWhoIsEnrolled(
        insured,
        new Set(whoIsEnrolled)
      );
      setFamilyUnit(familyUnit);
      setNumberOfDependents(numberOfDependents);
    }

    const medical = benefitsData.majorMedicalBenefit
      ? [toStorePlan(benefitsData.majorMedicalBenefit)]
      : [];
    const dental = (benefitsData.dentalBenefits ?? []).map(toStorePlan);
    const vision = (benefitsData.visionBenefits ?? []).map(toStorePlan);

    const applyEdit = (plans: EnrollmentAllowanceStorePlan[]) =>
      withEditedPremium(plans, editedBenefitId, editedBenefitPremium);

    setMedicalPlans(applyEdit(medical));
    setDentalPlans(applyEdit(dental));
    setVisionPlans(applyEdit(vision));
  }, [
    benefitsData,
    editedBenefitId,
    editedBenefitPremium,
    setFamilyUnit,
    setNumberOfDependents,
    setMedicalPlans,
    setDentalPlans,
    setVisionPlans,
  ]);
};
